import { AuthActionCreators, IDelete, IGet, IPost } from '@nkr/store/authorization';
import { ICoachDrawerDetail, ICoachDrawerLogEntry } from '@nkr/models/index';
import { MicrositeEmailType } from '@nkr/enums/index';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { useEffect, useState } from 'react';

export interface IChangeCoachStatusRequest {
    newCoachStatus: number;
    isCoachingDesired: boolean;
    nextFollowupDate: string;
    mostRecentContactDate: string;
    transplantDate: string;
}

interface IRetVal {
    data: ICoachDrawerDetail | null;
    loading: boolean;
    error: string;
    saveCoachStatus: (request: IChangeCoachStatusRequest) => Promise<void>;
    saveLogEntry: (id: number, content: string, actionType: number, isPinned: boolean) => Promise<void>;
    deleteLogEntry: (id: number) => Promise<void>;
    sendInvitationEmail: () => Promise<void>;
    sendDashboardLinkEmail: () => Promise<void>;
}

const useCoachDrawerDetail = (id: number | null): IRetVal => {
    const { dispatch } = useAppHookContext();
    const [data, setData] = useState<ICoachDrawerDetail | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!id) {
            setData(null);
            return;
        }

        setLoading(true);
        setError('');

        const fetchData = async () => {
            const config: IGet = { url: `${__API__}/microsite/coach/drawer-detail/${id}` };

            try {
                const response = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.success) {
                    setData(response.data.results);
                } else {
                    setError('Unable to load patient details');
                }
            } catch (err) {
                setError(err?.message ?? 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const saveCoachStatus = async (request: IChangeCoachStatusRequest) => {
        const config: IPost = {
            url: `${__API__}/microsite/${id}/change-coach-status`,
            data: request
        };

        await dispatch(AuthActionCreators.apiPost(config));

        setData(prev => prev ? {
            ...prev,
            coachStatus: request.newCoachStatus,
            coachStatusDescription: prev.coachStatusDescription,
            nextFollowupDate: request.nextFollowupDate || null,
            mostRecentCoachContactDateTime: request.mostRecentContactDate || null,
            isCoachingDesired: request.isCoachingDesired,
            transplantDate: request.transplantDate || null
        } : prev);
    };

    const saveLogEntry = async (id: number, content: string, actionType: number, isPinned: boolean) => {
        const config: IPost = {
            url: `${__API__}/microsite/coach/log-entry`,
            data: { id, micrositeRecipientId: data?.id, content, actionType, isPinned }
        };

        const response = await dispatch(AuthActionCreators.apiPost(config));

        if (response?.data?.success) {
            const saved: ICoachDrawerLogEntry = response.data.results;
            setData(prev => {
                if (!prev) {
                    return prev;
                }
                const existing = prev.coachLogEntries.find(e => e.id === saved.id);
                const updated = existing
                    ? prev.coachLogEntries.map(e => e.id === saved.id ? saved : e)
                    : [...prev.coachLogEntries, saved];
                return { ...prev, coachLogEntries: updated };
            });
        }
    };

    const deleteLogEntry = async (entryId: number) => {
        const config: IDelete = { url: `${__API__}/microsite/coach/log-entry/${entryId}` };

        await dispatch(AuthActionCreators.apiDelete(config));

        setData(prev => prev ? {
            ...prev,
            coachLogEntries: prev.coachLogEntries.filter(e => e.id !== entryId)
        } : prev);
    };

    const sendInvitationEmail = async () => {
        if (!id) {
            return;
        }
        const config: IGet = { url: `${__API__}/microsite/${id}/email/${MicrositeEmailType.RecipientInvitation}` };
        await dispatch(AuthActionCreators.apiGet(config));
    };

    const sendDashboardLinkEmail = async () => {
        if (!id) {
            return;
        }
        const config: IGet = { url: `${__API__}/microsite/${id}/email/${MicrositeEmailType.RecipientDashboardLink}` };
        await dispatch(AuthActionCreators.apiGet(config));
    };

    return { data, loading, error, saveCoachStatus, saveLogEntry, deleteLogEntry, sendInvitationEmail, sendDashboardLinkEmail };
}

export default useCoachDrawerDetail;
